<template>
  <b-modal
    ref="modal"
    :title="$t('influencer_panel.withdraw_modal_title')"
    :ok-title="$t('influencer_panel.withdraw_confirm')"
    :cancel-title="$t('action.cancel')"
    cancel-variant="outline-secondary"
    :ok-disabled="!canSubmit"
    centered
    @ok.prevent="submit"
    @hidden="reset"
  >
    <div class="withdraw__available">
      <span>{{ $t('influencer_panel.withdraw_available') }}</span>
      <hb-money :cents="maxCents" size="lg" tone="brand" />
    </div>

    <b-alert v-if="!usableKeys.length" variant="warning" show class="p-1">
      <small>{{ $t('influencer_panel.withdraw_no_pix') }}</small>
    </b-alert>

    <template v-else>
      <b-form-group :label="$t('influencer_panel.withdraw_amount')">
        <b-input-group prepend="R$">
          <b-form-input v-model="amount" type="number" step="0.01" min="0" :max="maxReais" />
        </b-input-group>
        <small v-if="amountTooHigh" class="text-danger">
          {{ $t('influencer_panel.withdraw_over_balance') }}
        </small>
      </b-form-group>

      <b-form-group :label="$t('influencer_panel.withdraw_destination')">
        <b-form-select v-model="pixKeyId" :options="keyOptions" />
      </b-form-group>
    </template>

    <p class="withdraw__note">
      <feather-icon icon="InfoIcon" size="14" />
      {{ $t('influencer_panel.withdraw_hint') }}
    </p>
  </b-modal>
</template>

<script>
import {
  BModal, BAlert, BFormGroup, BFormInput, BFormSelect, BInputGroup,
} from 'bootstrap-vue'
import HbMoney from '@/components/hubee/HbMoney.vue'

/**
 * Pedido de saque.
 *
 * Só chave `VA` entra na lista: `WalletWithdrawalService::resolvePixKey()` recusa
 * qualquer outra, e oferecer uma chave pendente aqui seria mandar o influencer bater
 * numa porta fechada.
 */
const PIX_TYPE_LABELS = {
  CP: 'CPF', CN: 'CNPJ', EM: 'E-mail', TE: 'Telefone', AL: 'Aleatória',
}

export default {
  name: 'WithdrawalRequestModal',
  components: {
    BModal, BAlert, BFormGroup, BFormInput, BFormSelect, BInputGroup, HbMoney,
  },
  props: {
    maxCents: { type: Number, default: 0 },
    pixKeys: { type: Array, default: () => [] },
  },
  data: () => ({ amount: '', pixKeyId: null }),
  computed: {
    usableKeys() {
      return this.pixKeys.filter(key => key.status === 'VA')
    },
    keyOptions() {
      return this.usableKeys.map(key => ({
        value: key.id,
        text: `${PIX_TYPE_LABELS[key.key_type] || key.key_type} · ${key.masked_key}`,
      }))
    },
    maxReais() {
      return (this.maxCents / 100).toFixed(2)
    },
    amountCents() {
      return Math.round(Number(this.amount || 0) * 100)
    },
    amountTooHigh() {
      return this.amountCents > this.maxCents
    },
    canSubmit() {
      return this.usableKeys.length > 0
        && this.amountCents > 0
        && !this.amountTooHigh
        && !!this.pixKeyId
    },
  },
  methods: {
    open() {
      this.amount = this.maxReais
      const preferred = this.usableKeys.find(key => key.is_default) || this.usableKeys[0]
      this.pixKeyId = preferred ? preferred.id : null
      this.$refs.modal.show()
    },
    close() {
      this.$refs.modal.hide()
    },
    reset() {
      this.amount = ''
      this.pixKeyId = null
    },
    submit() {
      this.$emit('confirm', { amountCents: this.amountCents, pixKeyId: this.pixKeyId })
    },
  },
}
</script>

<style scoped>
.withdraw__available {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  background: var(--tint-primary);
  border-radius: var(--radius);
  margin-bottom: 1.1rem;
  font-size: var(--fs-sm);
  color: var(--text-body);
}

.withdraw__note {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  font-size: var(--fs-sm);
  color: var(--text-muted);
}
</style>
